/**
 * The primary and secondary legs of an Internet service, as the Admin Center
 * captures them (CTX-1132).
 *
 * A redundant Internet — Finsus `13-2A3B40` on a dual Backhaul — rides two
 * VLANs, one per carrier, and each has its own P2P /31 and BGP neighbour. The
 * backend stores both legs in the same two JSON columns:
 *
 *   - `p2pAddressing`: `network/gateway/peerIp/mask` for the primary,
 *     the same keys with a `Secondary` suffix for the secondary, and
 *     `vlanSecondary` (the primary VLAN is the service's own `vlan` column).
 *   - `bgpConfig`: `neighborIpAddress/peeringType` and their `Secondary` pair.
 *
 * `PUT /resource-manager/ke-internet/:id` replaces each column whole, so the
 * form must send back every key it loaded. It used to rebuild `p2pAddressing`
 * from the four primary keys, and saving any Internet erased its secondary
 * leg and its `vlanSecondary` without a word. Everything here starts from the
 * loaded object and only overwrites the keys the form owns.
 */

export type P2PLegRole = "primary" | "secondary";

export interface P2PLeg {
  network: string;
  gateway: string;
  peerIp: string;
  mask: string | number | null;
}

/** `KontrolEdgeInternetEnterprise.p2pAddressing`, both legs. */
export interface InternetP2PAddressing extends P2PLeg {
  networkSecondary?: string;
  gatewaySecondary?: string;
  peerIpSecondary?: string;
  maskSecondary?: string | number | null;
  vlanSecondary?: number | null;
}

/** `KontrolEdgeInternetEnterprise.bgpConfig`, both neighbours. */
export interface InternetBgpConfig {
  peeringType?: string;
  neighborIpAddress?: string;
  peeringTypeSecondary?: string;
  neighborIpAddressSecondary?: string;
}

export const PEERING_TYPES = ["eBGP", "iBGP"] as const;

export const P2P_LEG_ROLES: readonly P2PLegRole[] = ["primary", "secondary"];

export const P2P_LEG_LABELS: Record<P2PLegRole, string> = {
  primary: "Primary",
  secondary: "Secondary",
};

export const emptyP2PLeg: P2PLeg = {
  network: "",
  gateway: "",
  peerIp: "",
  mask: null,
};

export const emptyP2PAddressing: InternetP2PAddressing = { ...emptyP2PLeg };

const SECONDARY_KEYS = {
  network: "networkSecondary",
  gateway: "gatewaySecondary",
  peerIp: "peerIpSecondary",
  mask: "maskSecondary",
} as const;

const VLAN_MIN = 1;
const VLAN_MAX = 4094;

/**
 * A mask is either dotted (`255.255.255.254`, kept as text) or a prefix
 * length (`31`, sent as a number). Anything else is passed through as typed
 * so the backend, not the form, decides whether it is valid.
 */
export function normalizeMask(
  value: string | number | null | undefined
): string | number | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (text === "") return null;
  if (text.includes(".")) return text;
  const asNumber = Number(text);
  return Number.isNaN(asNumber) ? text : asNumber;
}

export function isP2PLegEmpty(leg: P2PLeg): boolean {
  return (
    leg.network.trim() === "" &&
    leg.gateway.trim() === "" &&
    leg.peerIp.trim() === "" &&
    normalizeMask(leg.mask) === null
  );
}

export function isP2PLegComplete(leg: P2PLeg): boolean {
  return (
    leg.network.trim() !== "" &&
    leg.gateway.trim() !== "" &&
    leg.peerIp.trim() !== "" &&
    normalizeMask(leg.mask) !== null
  );
}

/** The leg as the form edits it, or null when that leg was never captured. */
export function p2pLegOf(
  p2p: InternetP2PAddressing,
  role: P2PLegRole
): P2PLeg | null {
  const leg: P2PLeg =
    role === "primary"
      ? {
          network: p2p.network ?? "",
          gateway: p2p.gateway ?? "",
          peerIp: p2p.peerIp ?? "",
          mask: p2p.mask ?? null,
        }
      : {
          network: p2p.networkSecondary ?? "",
          gateway: p2p.gatewaySecondary ?? "",
          peerIp: p2p.peerIpSecondary ?? "",
          mask: p2p.maskSecondary ?? null,
        };
  return isP2PLegEmpty(leg) ? null : leg;
}

/**
 * `p2p` with one leg replaced. A null secondary removes its keys instead of
 * leaving empty strings behind, so a service that never had one reads the
 * same as before. Every other key, `vlanSecondary` included, is kept.
 */
export function withP2PLeg(
  p2p: InternetP2PAddressing,
  role: P2PLegRole,
  leg: P2PLeg | null
): InternetP2PAddressing {
  if (role === "primary") {
    return { ...p2p, ...(leg ?? emptyP2PLeg) };
  }
  const next: InternetP2PAddressing = { ...p2p };
  if (leg === null) {
    for (const key of Object.values(SECONDARY_KEYS)) delete next[key];
    return next;
  }
  next.networkSecondary = leg.network;
  next.gatewaySecondary = leg.gateway;
  next.peerIpSecondary = leg.peerIp;
  next.maskSecondary = leg.mask;
  return next;
}

/** `p2p` with its secondary VLAN set, or removed when null. */
export function withSecondaryVlan(
  p2p: InternetP2PAddressing,
  vlanSecondary: number | null
): InternetP2PAddressing {
  const next: InternetP2PAddressing = { ...p2p };
  if (vlanSecondary === null) {
    delete next.vlanSecondary;
  } else {
    next.vlanSecondary = vlanSecondary;
  }
  return next;
}

/** What the API returned, with the primary keys the form always shows. */
export function p2pAddressingFromApi(
  raw: Partial<InternetP2PAddressing> | null | undefined
): InternetP2PAddressing {
  if (!raw) return { ...emptyP2PAddressing };
  return {
    ...raw,
    network: raw.network ?? "",
    gateway: raw.gateway ?? "",
    peerIp: raw.peerIp ?? "",
    mask: raw.mask ?? null,
  };
}

/** The column as it is sent back: every loaded key, both masks normalized. */
export function p2pAddressingForPayload(
  p2p: InternetP2PAddressing
): InternetP2PAddressing {
  const payload: InternetP2PAddressing = {
    ...p2p,
    mask: normalizeMask(p2p.mask),
  };
  if (p2pLegOf(p2p, "secondary") !== null) {
    payload.maskSecondary = normalizeMask(p2p.maskSecondary);
  }
  return payload;
}

/**
 * Why the P2P legs cannot be saved, or null. A leg is all or nothing; the
 * primary is required only on create, and a secondary needs a primary.
 */
export function p2pAddressingError(
  p2p: InternetP2PAddressing,
  { requirePrimary }: { requirePrimary: boolean }
): string | null {
  const primary = p2pLegOf(p2p, "primary");
  const secondary = p2pLegOf(p2p, "secondary");

  if (primary === null && (requirePrimary || secondary !== null)) {
    return "Fill in the primary P2P leg (Network, Gateway, Peer IP and Mask).";
  }
  if (primary !== null && !isP2PLegComplete(primary)) {
    return "Fill in every field of the primary P2P leg (Network, Gateway, Peer IP and Mask), or remove it.";
  }
  if (secondary !== null && !isP2PLegComplete(secondary)) {
    return "Fill in every field of the secondary P2P leg (Network, Gateway, Peer IP and Mask), or remove it.";
  }
  return null;
}

/** The secondary VLAN as typed: null for empty, NaN for anything not numeric. */
export function parseVlanInput(value: string): number | null {
  const text = value.trim();
  return text === "" ? null : Number(text);
}

/**
 * Why the secondary VLAN cannot be saved, or null. It is optional, but when
 * present it must be a real VLAN and not the primary one: the two legs of a
 * dual Backhaul are told apart by their VLAN (CTX-1134).
 */
export function secondaryVlanError(
  vlan: number | null,
  vlanSecondary: number | null | undefined
): string | null {
  if (vlanSecondary === null || vlanSecondary === undefined) return null;
  if (
    !Number.isInteger(vlanSecondary) ||
    vlanSecondary < VLAN_MIN ||
    vlanSecondary > VLAN_MAX
  ) {
    return `Secondary VLAN must be a whole number from ${VLAN_MIN} to ${VLAN_MAX}.`;
  }
  if (vlan !== null && vlanSecondary === vlan) {
    return "Secondary VLAN must differ from the primary VLAN.";
  }
  return null;
}

export function bgpConfigFromApi(
  raw: InternetBgpConfig | null | undefined
): InternetBgpConfig | null {
  return raw ? { ...raw } : null;
}

/** `bgp` with one field set; an empty value removes the key. */
export function withBgpField(
  bgp: InternetBgpConfig | null,
  field: keyof InternetBgpConfig,
  value: string
): InternetBgpConfig {
  const next: InternetBgpConfig = { ...(bgp ?? {}) };
  const text = value.trim();
  if (text === "") {
    delete next[field];
  } else {
    next[field] = text;
  }
  return next;
}

/**
 * The column as it is sent back. Undefined — the key left out of the PUT —
 * when the service had no BGP and nobody typed one, so an untouched static
 * Internet is not given an empty `{}`.
 */
export function bgpConfigForPayload(
  bgp: InternetBgpConfig | null
): InternetBgpConfig | undefined {
  if (bgp === null) return undefined;
  return Object.keys(bgp).length === 0 ? undefined : bgp;
}
